import './PrivacyPage.scss';

export function PrivacyPage() {
  return (
    <main className="privacy-page">
      <div className="privacy-page__container">
        <header className="privacy-page__header">
          <h1 className="privacy-page__title">Privacy Policy</h1>
          <p className="privacy-page__subtitle">
            Last updated: February 25, 2026
          </p>
        </header>

        <div className="privacy-page__intro">
          <p className="privacy-page__text">
            This Privacy Policy explains how we collect, use, and protect information
            when you use our Chrome extension and related services ("the Service").
          </p>
          <p className="privacy-page__text">
            By using the Service, you agree to the practices described in this policy.
          </p>
        </div>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">Information We Collect</h2>

          <div className="privacy-page__card">
            <h3 className="privacy-page__card-title">Information You Provide</h3>
            <p className="privacy-page__text">
              When you use the extension, you may choose to share HTTP request data, which can include:
            </p>
            <ul className="privacy-page__list">
              <li>URLs</li>
              <li>HTTP methods</li>
              <li>Headers</li>
              <li>Request bodies</li>
              <li>Response data</li>
            </ul>
            <p className="privacy-page__text privacy-page__text--highlight">
              Sensitive fields such as authorization tokens are masked by default.
            </p>
          </div>

          <div className="privacy-page__card">
            <h3 className="privacy-page__card-title">Automatically Collected Information</h3>
            <p className="privacy-page__text">We do not collect:</p>
            <div className="privacy-page__no-collect-grid">
              <div className="privacy-page__no-collect-item">
                <span className="privacy-page__no-collect-icon">&#x1f6ab;</span>
                <span>Browsing history</span>
              </div>
              <div className="privacy-page__no-collect-item">
                <span className="privacy-page__no-collect-icon">&#x1f6ab;</span>
                <span>Personal identifiers</span>
              </div>
              <div className="privacy-page__no-collect-item">
                <span className="privacy-page__no-collect-icon">&#x1f6ab;</span>
                <span>Analytics or tracking data</span>
              </div>
              <div className="privacy-page__no-collect-item">
                <span className="privacy-page__no-collect-icon">&#x1f6ab;</span>
                <span>Keystrokes or user behavior outside the extension</span>
              </div>
            </div>
            <p className="privacy-page__text privacy-page__text--muted">
              The extension only accesses data necessary to perform its core functionality.
            </p>
          </div>
        </section>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">How We Use Information</h2>

          <div className="privacy-page__card">
            <p className="privacy-page__text">We use the collected data solely to:</p>
            <div className="privacy-page__use-grid">
              <div className="privacy-page__use-item">
                <span className="privacy-page__use-icon">&#x1f4cb;</span>
                <span>Display shared HTTP requests</span>
              </div>
              <div className="privacy-page__use-item">
                <span className="privacy-page__use-icon">&#x1f517;</span>
                <span>Generate shareable links (when requested)</span>
              </div>
              <div className="privacy-page__use-item">
                <span className="privacy-page__use-icon">&#x1f91d;</span>
                <span>Enable collaboration between developers</span>
              </div>
            </div>

            <div className="privacy-page__divider" />

            <p className="privacy-page__text">We do not use your data for:</p>
            <ul className="privacy-page__list privacy-page__list--crossed">
              <li>Advertising</li>
              <li>Tracking</li>
              <li>Profiling</li>
              <li>Selling or sharing with third parties</li>
            </ul>
          </div>
        </section>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">Data Sharing</h2>

          <div className="privacy-page__card">
            <p className="privacy-page__text">
              We do not sell, rent, or share your data with third parties.
            </p>
            <p className="privacy-page__text">Shared request data is only accessible:</p>
            <ul className="privacy-page__list">
              <li>By you</li>
              <li>By anyone you explicitly share a link with</li>
            </ul>
          </div>
        </section>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">Data Retention</h2>

          <div className="privacy-page__card">
            <div className="privacy-page__retention-grid">
              <div className="privacy-page__retention-item">
                <span className="privacy-page__retention-icon">&#x1f4be;</span>
                <div>
                  <strong>Temporary storage</strong>
                  <p className="privacy-page__text privacy-page__text--muted">
                    Shared data is stored only as long as necessary to provide the service
                  </p>
                </div>
              </div>
              <div className="privacy-page__retention-item">
                <span className="privacy-page__retention-icon">&#x23f3;</span>
                <div>
                  <strong>Auto-expiry</strong>
                  <p className="privacy-page__text privacy-page__text--muted">
                    Links may expire automatically based on your settings
                  </p>
                </div>
              </div>
              <div className="privacy-page__retention-item">
                <span className="privacy-page__retention-icon">&#x1f5d1;</span>
                <div>
                  <strong>User control</strong>
                  <p className="privacy-page__text privacy-page__text--muted">
                    You can delete shared data at any time
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">Security</h2>

          <div className="privacy-page__card">
            <p className="privacy-page__text">
              We take reasonable measures to protect your data, including:
            </p>
            <div className="privacy-page__security-grid">
              <div className="privacy-page__security-item">
                <span className="privacy-page__security-icon">&#x1f512;</span>
                <span>Encryption at rest</span>
              </div>
              <div className="privacy-page__security-item">
                <span className="privacy-page__security-icon">&#x1f6e1;</span>
                <span>Secure access controls</span>
              </div>
              <div className="privacy-page__security-item">
                <span className="privacy-page__security-icon">&#x1f4c9;</span>
                <span>Minimal data retention</span>
              </div>
            </div>
            <p className="privacy-page__text privacy-page__text--muted">
              However, no system can be 100% secure.
            </p>
          </div>
        </section>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">Permissions</h2>

          <div className="privacy-page__card">
            <p className="privacy-page__text">The extension requests access only to:</p>
            <div className="privacy-page__permission-badge">
              Chrome DevTools Network requests
            </div>
            <p className="privacy-page__text privacy-page__text--muted">
              These permissions are required to provide the core functionality of the Service.
            </p>
          </div>
        </section>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">Children's Privacy</h2>

          <div className="privacy-page__card">
            <p className="privacy-page__text">
              The Service is not intended for users under the age of 13.
              We do not knowingly collect personal information from children.
            </p>
          </div>
        </section>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">Changes to This Policy</h2>

          <div className="privacy-page__card">
            <p className="privacy-page__text">
              We may update this Privacy Policy from time to time.
              Any changes will be posted on this page with an updated "Last updated" date.
            </p>
          </div>
        </section>

        <section className="privacy-page__section">
          <h2 className="privacy-page__section-title">Contact</h2>

          <div className="privacy-page__card privacy-page__card--contact">
            <p className="privacy-page__text">
              If you have questions or concerns about this Privacy Policy, please contact:
            </p>
            <div className="privacy-page__contact-item">
              <span className="privacy-page__contact-label">Email</span>
              <a href="mailto:privacy@shareq.site" className="privacy-page__contact-link">
                privacy@shareq.site
              </a>
            </div>
          </div>
        </section>

      </div>
    </main>
  );
}
